import { noop } from '@sgme/fp';
import { unminifyError, unminifyStackTrace } from '@sgme/stacktrace';
import type { ErrorBoundaryContext } from 'components/common-form/builder/form/errorBoundary';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { createContext, ErrorInfo, useCallback } from 'react';
import { errorInBoundaryAutomaticEmailThunk } from 'state/appCrashed/appCrash.thunks';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

export type ReportErrorCallback = (error: unknown, info: ErrorInfo, context?: ErrorBoundaryContext) => void;

export const loggerErrorHandler = async (
  error: unknown,
  { componentStack }: ErrorInfo,
  context?: ErrorBoundaryContext,
) => {
  const err = await unminifyError(error);
  err.componentStack = await unminifyStackTrace(componentStack);
  getFrontLogger().error('An error was catched by errorBoundary {@FrontError}', { ...err, ...context });
};

export function useReportErrorCallback(): ReportErrorCallback {
  const dispatch = useThunkDispatch();

  return useCallback(
    (error: unknown, errorInfo: ErrorInfo, context?: ErrorBoundaryContext) => {
      dispatch(errorInBoundaryAutomaticEmailThunk(error, errorInfo.componentStack, context));
    },
    [dispatch],
  );
}

export const ErrorReportingContext = createContext<ReportErrorCallback>(noop);
ErrorReportingContext.displayName = 'ErrorReportingContext';
